/**
 * The standalone iframe app — vanilla entry over the SDK engine.
 *
 * One transport for every delivery mode: this entry composes the same
 * DealerSDK that npm/SDK consumers get (IframeSDKService), the shared vanilla
 * widget renderer, and the framework-free welcome conductor (ADR 0039). The
 * parent postMessage contract (SERVICE_READY / CONFIG_ACCEPTED / GAME_EVENT /
 * WELCOME_* / AI events) is preserved verbatim from the React entry it
 * replaces.
 */

import { MessageRouter } from '../core/logic/message-router';
import { configureLivekitLogging } from '../core/logic/livekit-logging';
import {
  GAME_EVENT_ENVELOPE_TYPE,
  validateGameEventEnvelope,
} from '../core/logic/game-event-validator';
import { createGameEventDispatcher } from '../core/logic/game-event-dispatch';
import { probeAudioBlocked } from '../core/logic/audio-availability';
import { createReconnectScheduler, type ReconnectScheduler } from '../core/logic/reconnect-policy';
import { deriveWelcomeMode, type WelcomeMode } from '../core/logic/widget-mode';
import { resolveLanguage, resolveLanguageWithWarning, translate } from '../core/i18n';
import { ServiceMode } from '../core/types/service-mode';
import {
  POSTMESSAGE_PROTOCOL_VERSION,
  resolveProtocolVersion,
  type InboundMessageType,
} from '../core/types/message-types';
import { isTerminalSessionError, type AgentState } from '../sdk';
import { createDealerWidget, createWidgetRenderer, type WidgetHandle } from '../widget';
import { flowLog } from '../welcome-flow-log';
import { getLogger, getLogLevel } from '../logger';
import { readRuntimeString } from '../core/app-config';
import { SDK_VERSION } from '../core/generated/sdk-version';

import { createWelcomeConductor } from './welcome-conductor';
import { createAudioUnlock } from './audio-unlock';
import { createInertStateSource } from './inert-state-source';
import { IframeSDKService } from './iframe-sdk-service';

// Import styles
import './standalone-panel.css';
import '../components/app-shell.css';
import '../styles/interfaces/voice-interface.css';

const logger = getLogger('StandaloneApp');
// The SDK defaults to a NOOP logger so third-party embedders opt in to console
// output. In our own hosts we always want it: without this the whole SDK layer
// (connect, transport, agent lifecycle) is silent in our builds.
const sdkLogger = getLogger('DealerSDK');

/**
 * API server origin: parent-supplied `serviceUrl` wins, then `/config.js`.
 *
 * The literal below is the compiled floor and is deliberately localhost-only —
 * the one place a URL may be hardcoded (ADR 0046). Deployed environments always
 * supply `apiServerUrl` via `/config.js`, so this is reached only when running
 * the app straight off a dev server.
 */
const LOCALHOST_API_DEV_FLOOR = 'http://localhost:4500';

function defaultServiceUrl(): string {
  return readRuntimeString('apiServerUrl') || LOCALHOST_API_DEV_FLOOR;
}

interface ServiceConfig {
  sessionToken?: string;
  serviceUrl: string;
  languageId?: string;
}

// ---------- Parent messaging ----------

let parentOriginLocked: string | null = null;

function resolveParentOrigin(): string | null {
  if (parentOriginLocked) {
    return parentOriginLocked;
  }
  if (!document.referrer) {
    return null;
  }
  try {
    const origin = new URL(document.referrer).origin;
    parentOriginLocked = origin;
    return origin;
  } catch (error) {
    logger.warn('Failed to resolve parent origin from referrer', {
      referrer: document.referrer,
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

function postToParent(message: Record<string, unknown>): void {
  const targetOrigin = parentOriginLocked ?? resolveParentOrigin() ?? '*';
  window.parent.postMessage(message, targetOrigin);
}

// ---------- App state ----------

const gameEventDispatcher = createGameEventDispatcher();

let config: ServiceConfig | null = null;
let service: IframeSDKService | null = null;
let connected = false;
let transportError: string | null = null;
let showDealerVisualizer = false;
// SET_DEALER_MUTE that arrived before the SDK connected — applied post-connect.
let pendingMute: boolean | null = null;
// Transport-drop recovery (#478); created per service in boot().
let reconnect: ReconnectScheduler | null = null;

// DOM skeleton (built once in boot) + widget mounts.
let containerEl: HTMLDivElement | null = null;
let roomEl: HTMLDivElement | null = null;
let liveMountEl: HTMLDivElement | null = null;
let overlayEl: HTMLDivElement | null = null;
let welcomeMountEl: HTMLDivElement | null = null;
let errorEl: HTMLDivElement | null = null;
let errorMessageEl: HTMLDivElement | null = null;
let liveWidget: WidgetHandle | null = null;
let welcomeWidget: WidgetHandle | null = null;
let welcomeWidgetMode: WelcomeMode | null = null;

const uiLanguage = () => resolveLanguage(config?.languageId ?? 'en');

/**
 * The iframe is the only place that knows the language list (ADR 0061): the lib
 * forwards `languageId` untouched, so an unknown or missing code is diagnosed
 * here, once, with the raw value the host actually sent.
 */
function warnOnUnresolvedLanguage(languageId: unknown): void {
  resolveLanguageWithWarning(languageId, (message, context) => logger.warn(message, context));
}

// ---------- Welcome ceremony + audio unlock ----------
// (audioUnlock's report closure runs only after a request — `conductor` is
// initialized by then despite being declared second.)

const audioUnlock = createAudioUnlock({
  startAudio: () =>
    service ? service.startAudio() : Promise.reject(new Error('SDK not available')),
  canPlaybackAudio: () => service?.getState().canPlaybackAudio ?? false,
  report: (canPlay) => conductor.audioPlayableChanged(canPlay),
});

const conductor = createWelcomeConductor({
  probeAudioBlocked,
  post: (message) => postToParent(message),
  startAudio: () => audioUnlock.request(),
  onChange: () => renderShell(),
});

// ---------- Parent-facing AI events (vocabulary preserved from the React entry) ----------

let lastAgentState: AgentState | null = null;
function emitAgentState(state: AgentState): void {
  if (state === lastAgentState) return;
  lastAgentState = state;
  logger.debug('Agent state:', state);
  postToParent({ type: 'AGENT_STATE_CHANGE', payload: { state } });
  if (state === 'speaking') {
    postToParent({ type: 'AI_EVENT', payload: { name: 'ai-speaking:start' } });
  } else if (state === 'listening' || state === 'thinking') {
    postToParent({ type: 'AI_EVENT', payload: { name: 'ai-speaking:stop' } });
  }
}

let lastServiceMode: ServiceMode | null = null;
function emitServiceMode(mode: ServiceMode): void {
  if (mode === lastServiceMode) return;
  lastServiceMode = mode;
  const lang = uiLanguage();
  const label =
    mode === ServiceMode.Normal
      ? translate(lang, 'dealerActive')
      : mode === ServiceMode.Paused
        ? translate(lang, 'dealerAway')
        : translate(lang, 'textOnly');
  postToParent({ type: 'SERVICE_MODE_CHANGE', payload: { mode, label } });
}

function bindServiceEvents(svc: IframeSDKService): void {
  svc.on('ready', (state) => {
    logger.info('Agent ready - emitting AGENT_READY event');
    postToParent({ type: 'AGENT_READY', payload: { state } });
  });
  svc.on('agentState', emitAgentState);
  svc.on('serviceMode', emitServiceMode);
  svc.on('audioPlaybackChanged', () => audioUnlock.handlePlaybackChanged());
  svc.on('disconnected', ({ reason }) => {
    logger.warn('SDK transport disconnected', { reason });
    emitAgentState('disconnected');
    // The session outlives the room (token valid, /connect recreates the room
    // lazily), so a dropped transport is recoverable — until now nothing on the
    // client tried (#478). Playback-unlock taps queue until the reconnect lands.
    audioUnlock.notifyDisconnected();
    reconnect?.onDisconnected(reason);
  });
  svc.on('error', ({ code, message }) => {
    logger.error('SDK error', { code, message });
  });
  // Initial report (the React bridge emitted the mount-time state the same way).
  emitAgentState(svc.getState().agentState);
}

// ---------- Rendering ----------

function buildSkeleton(): void {
  const root = document.getElementById('root');
  if (!root) {
    throw new Error('#root element missing');
  }
  root.textContent = '';

  containerEl = document.createElement('div');
  containerEl.className = 'ai-service-container';

  // Mirrors the replaced React tree so app-shell.css applies unchanged:
  // .lk-room-container > .ai-service-content > .voice-interface mount.
  roomEl = document.createElement('div');
  roomEl.className = 'lk-room-container';
  const content = document.createElement('div');
  content.className = 'ai-service-content';
  liveMountEl = document.createElement('div');
  liveMountEl.className = 'voice-interface';
  content.appendChild(liveMountEl);
  roomEl.appendChild(content);

  overlayEl = document.createElement('div');
  overlayEl.className = 'ai-service-welcome-overlay';
  welcomeMountEl = document.createElement('div');
  welcomeMountEl.style.width = '100%';
  overlayEl.appendChild(welcomeMountEl);

  errorEl = document.createElement('div');
  errorEl.style.display = 'none';
  errorEl.style.height = '100%';
  errorEl.style.alignItems = 'center';
  errorEl.style.justifyContent = 'center';
  errorEl.style.textAlign = 'center';
  errorMessageEl = document.createElement('div');
  errorMessageEl.style.color = '#ef4444';
  errorMessageEl.style.marginBottom = '1rem';
  const retryButton = document.createElement('button');
  retryButton.textContent = translate(uiLanguage(), 'retry');
  retryButton.style.cssText =
    'padding:0.5rem 1rem;background:#3b82f6;color:#fff;border:none;border-radius:0.25rem;cursor:pointer';
  retryButton.addEventListener('click', () => window.location.reload());
  const errorInner = document.createElement('div');
  errorInner.append(errorMessageEl, retryButton);
  errorEl.appendChild(errorInner);

  containerEl.append(roomEl, overlayEl, errorEl);
  root.appendChild(containerEl);
}

function renderShell(): void {
  if (!containerEl || !roomEl || !overlayEl || !errorEl || !welcomeMountEl) {
    return; // pre-boot: the conductor may tick before INITIALIZE_SERVICE
  }
  const view = conductor.getView();
  const isWelcome = view.phase === 'welcome';

  containerEl.className =
    'ai-service-container' +
    (isWelcome ? ' ai-service-welcome' : '') +
    (isWelcome && view.exiting ? ' ai-service-welcome-exiting' : '');

  // The room subtree appears once connected (the React entry gated it on the
  // token the same way); a transport failure hides it in both paths.
  roomEl.style.display = connected && transportError === null ? '' : 'none';

  // Welcome overlay: present only during the welcome phase; the widget mounts
  // only once the gate resolves, so it appears once in its final state.
  overlayEl.style.display = isWelcome ? '' : 'none';
  const wantWelcomeWidget = isWelcome && view.resolved;
  if (wantWelcomeWidget) {
    const mode = deriveWelcomeMode(transportError !== null, view.audioBlocked);
    if (!welcomeWidget) {
      welcomeWidget = createWidgetRenderer(welcomeMountEl, createInertStateSource(), {
        language: config?.languageId,
        mode,
        onCtaTap: () => conductor.ctaTap(),
      });
    } else if (mode !== welcomeWidgetMode) {
      welcomeWidget.setMode(mode);
    }
    welcomeWidgetMode = mode;
  } else if (welcomeWidget) {
    welcomeWidget.destroy();
    welcomeWidget = null;
    welcomeWidgetMode = null;
  }

  // A transport failure outside welcome shows the retry chrome (during
  // welcome the degraded overlay covers it — same as the old React entry).
  const showError = !isWelcome && transportError !== null;
  if (showError && errorMessageEl) {
    errorMessageEl.textContent = `${translate(uiLanguage(), 'errorPrefix')} ${transportError}`;
  }
  errorEl.style.display = showError ? 'flex' : 'none';
}

function mountLiveWidget(svc: IframeSDKService): void {
  if (!liveMountEl) return;
  liveWidget?.destroy();
  liveMountEl.textContent = '';
  liveWidget = createDealerWidget(liveMountEl, svc, {
    language: config?.languageId,
    mode: 'live',
    showDealerVisualizer,
    onPTTStateChange: (state) => {
      logger.debug('PTT state changed:', state);
      postToParent({
        type: 'PTT_STATE_CHANGE',
        payload: { holding: state.isHolding, canceled: state.isCanceled },
      });
    },
    // Additive (ADR 0037): lets the lib keep the iframe above a mobile keyboard (ADR 0065).
    onTextInputChange: (open) => postToParent({ type: 'TEXT_INPUT_STATE', payload: { open } }),
  });
}

// ---------- Boot ----------

function boot(cfg: ServiceConfig, rawLanguageId: unknown = cfg.languageId): void {
  config = cfg;
  // The raw value, not cfg's: the message boundary narrows a non-string to
  // undefined, and "missing" would then hide a wrong-typed languageId.
  warnOnUnresolvedLanguage(rawLanguageId);
  // Locale-correct casing (Turkish İ/ı), hyphenation and screen-reader voice.
  document.documentElement.lang = uiLanguage();
  buildSkeleton();
  renderShell();

  if (!cfg.sessionToken) {
    transportError = 'Session token required';
    logger.error('No session token provided');
    renderShell();
    return;
  }

  const svc = new IframeSDKService({
    serviceUrl: cfg.serviceUrl,
    sessionToken: cfg.sessionToken,
    logger: sdkLogger,
  });
  service = svc;
  bindServiceEvents(svc);
  mountLiveWidget(svc);

  reconnect?.destroy();
  const scheduler = createReconnectScheduler({
    connect: () => svc.initialize(),
    isPageActive: () => document.visibilityState === 'visible' && navigator.onLine !== false,
    onReconnected: () => {
      logger.info('SDK transport reconnected');
      transportError = null;
      audioUnlock.notifyConnected();
      emitServiceMode(svc.getState().serviceMode);
      renderShell();
    },
    onGaveUp: (error) => {
      transportError = error instanceof Error ? error.message : String(error);
      logger.error('SDK reconnect gave up', { error: transportError });
      renderShell();
    },
    // A terminal /connect verdict (session closed server-side — operator
    // relaunch, inactivity expiry, sweeper) is not a transport failure: stay
    // quiet like the pre-#478 client did — no transportError, no retry chrome.
    isTerminalError: isTerminalSessionError,
    onTerminal: (error) => {
      // warn, not info: this is the one ending that leaves a quiet widget, so
      // it must be findable in logs if the verdict was ever wrong (#485).
      logger.warn('Session closed server-side — not reconnecting', {
        lastError: error instanceof Error ? error.message : String(error),
      });
    },
  });
  reconnect = scheduler;
  const onPageActive = (): void => scheduler.onPageActive();
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) onPageActive();
  });
  window.addEventListener('online', onPageActive);
  window.addEventListener('pageshow', onPageActive);

  // Consume the direct-dispatch game-event channel (queued events flush here).
  gameEventDispatcher.register((event) => {
    const parsed = validateGameEventEnvelope(event.data);
    if (!parsed) {
      logger.warn('Invalid game event format:', event.data);
      return;
    }
    logger.debug('Processing game event:', parsed.type);
    svc
      .sendRawGameEvent(parsed.type, parsed.data)
      .then((result) => logger.debug('RPC response:', result.success ? 'success' : 'failed'))
      .catch((error) => logger.error('Failed to handle game event:', error));
  });

  svc
    .initialize()
    .then(() => {
      connected = true;
      audioUnlock.notifyConnected();
      if (pendingMute !== null) {
        const muted = pendingMute;
        pendingMute = null;
        void svc.setDealerMute(muted).catch((error) => {
          logger.error('Deferred dealer mute failed', {
            error: error instanceof Error ? error.message : String(error),
          });
        });
      }
      emitServiceMode(svc.getState().serviceMode);
      renderShell();
    })
    .catch((error) => {
      transportError = error instanceof Error ? error.message : String(error);
      logger.error('SDK initialize failed', { error: transportError });
      renderShell();
    });
}

// ---------- Parent message channel ----------

function initMessageChannel(): void {
  logger.debug('AI Service Standalone initializing...');

  const router = new MessageRouter<InboundMessageType>({
    handlers: {
      INITIALIZE_SERVICE: (payload) => {
        logger.debug('Config received');
        const p = (payload as Record<string, unknown>) || {};
        const parentProtocolVersion = resolveProtocolVersion(p.protocolVersion);
        if (parentProtocolVersion !== POSTMESSAGE_PROTOCOL_VERSION) {
          logger.warn(
            `Parent lib speaks postMessage protocol v${parentProtocolVersion}, iframe speaks v${POSTMESSAGE_PROTOCOL_VERSION} — continuing (additive-only protocol, ADR 0037)`
          );
        }
        const runtimeServiceUrl =
          typeof p.serviceUrl === 'string' && p.serviceUrl.trim().length > 0
            ? (p.serviceUrl as string).trim()
            : defaultServiceUrl();
        const cfg: ServiceConfig = {
          sessionToken: p.sessionToken as string | undefined,
          serviceUrl: runtimeServiceUrl,
          languageId: typeof p.languageId === 'string' ? p.languageId : undefined,
        };
        if (config) {
          // The lib sends this once per iframe; a repeat can't re-init a live SDK.
          logger.warn('INITIALIZE_SERVICE received again — already configured, ignoring');
        } else {
          boot(cfg, p.languageId);
        }
        postToParent({ type: 'CONFIG_ACCEPTED', payload: { success: true } });
      },
      [GAME_EVENT_ENVELOPE_TYPE]: (payload) => {
        const p = payload as { type?: string; data?: Record<string, unknown> } | undefined;
        // Debug level: pairs with the lib's "Posting GAME_EVENT" and the widget's
        // "Dispatching game event" logs to expose where an event is lost.
        logger.debug('GAME_EVENT received from parent:', p?.type);
        gameEventDispatcher.dispatch({
          data: { type: GAME_EVENT_ENVELOPE_TYPE, payload: p },
        });
      },
      ENTER_WELCOME: () => {
        conductor.enterWelcome();
      },
      EXIT_WELCOME: () => {
        logger.debug('EXIT_WELCOME received from parent');
        conductor.exitWelcome();
      },
      SET_DEALER_MUTE: (payload) => {
        const muted = (payload as { muted?: boolean } | undefined)?.muted ?? false;
        logger.debug('External mute request:', muted);
        if (service && connected) {
          void service.setDealerMute(muted).catch((error) => {
            logger.error('setDealerMute failed', {
              error: error instanceof Error ? error.message : String(error),
            });
          });
        } else {
          pendingMute = muted;
        }
      },
      START_AUDIO: () => {
        flowLog('START_AUDIO received → audio unlock requested');
        audioUnlock.request();
      },
      UPDATE_WIDGET_PROPS: (payload) => {
        logger.debug('Widget props update:', payload);
        const p = payload as { showDealerVisualizer?: boolean } | undefined;
        if (
          p?.showDealerVisualizer !== undefined &&
          p.showDealerVisualizer !== showDealerVisualizer
        ) {
          showDealerVisualizer = p.showDealerVisualizer;
          if (service) {
            mountLiveWidget(service); // rare runtime knob — remount like the React host did
          }
        }
      },
    },
  });

  window.addEventListener('message', (event: MessageEvent) => {
    // Framing-specific guard: only accept messages from our parent window
    if (event.source !== window.parent) {
      return;
    }
    // Lock the origin to the router's view so postToParent uses the correct target
    router.route({ data: event.data, origin: event.origin });
    parentOriginLocked = router.getState().lockedOrigin;
  });

  // Send ready message to parent window with retries
  const sendReadyMessage = () => {
    try {
      postToParent({
        type: 'SERVICE_READY',
        payload: { protocolVersion: POSTMESSAGE_PROTOCOL_VERSION },
      });
      logger.debug('Sent ready message to parent window');
    } catch (error) {
      logger.error('Failed to send ready message:', error);
    }
  };
  sendReadyMessage();
  const intervalId = setInterval(sendReadyMessage, 500);
  setTimeout(() => clearInterval(intervalId), 5000);
}

function initialConfig(): ServiceConfig | null {
  const isInIframe = window.self !== window.top;

  // Hardcoded dev config only when running standalone (not embedded).
  // Embedded contexts always wait for INITIALIZE_SERVICE from the parent.
  if (process.env.NODE_ENV === 'development' && !isInIframe) {
    logger.info('Standalone development mode (direct access) - using default config');
    return {
      serviceUrl: defaultServiceUrl(),
      languageId: new URLSearchParams(window.location.search).get('lang') ?? 'en',
    };
  }

  logger.info(`Waiting for INITIALIZE_SERVICE message (iframe: ${isInIframe})`);
  return null;
}

// Initialize the app. The stamp is at `info` so it survives prod defaults:
// without it a console screenshot cannot be attributed to an environment or an
// artifact (dev vs prod vs a stale cached bundle all looked identical).
logger.info(
  `Starting AI Service Standalone — env=${readRuntimeString('env') ?? 'unknown'} ` +
    `build=${readRuntimeString('build') ?? 'unknown'} version=${SDK_VERSION} ` +
    `logLevel=${getLogLevel()}`
);
// Quiet livekit-client's verbose INFO logs (room-join token in the signal URL,
// connection/participant details) — verbose only under ?log=debug.
configureLivekitLogging();
initMessageChannel();
const initial = initialConfig();
if (initial) {
  boot(initial);
}
